import { Disclosure } from "../core/disclosure";
import { PluginRegistry } from "../core/pluginRegistry";

export class FilterAutocomplete extends Disclosure {
  private input: HTMLInputElement | null = null;
  private listItems: NodeListOf<HTMLElement> | null = null;
  private documentClickHandler: (event: MouseEvent) => void;

  constructor(element: HTMLElement) {
    super(element, {
      triggerSelector: "[data-trigger='autocomplete']",
      contentSelector: "[data-content='autocomplete']",
      eventNameRaw: "filterAutocomplete",
    });
    this.documentClickHandler = this.handleDocumentClick.bind(this);
  }

  init(): void {
    super.initDisclosure();

    this.input = this.element.querySelector("input[type='text']");
    this.listItems = this.element.querySelectorAll("[data-option]");

    this.bindAutocompleteEvents();
    document.addEventListener("click", this.documentClickHandler);

    this.element.addEventListener("filterAutocomplete:reset", ((
      e: CustomEvent,
    ) => {
      const silent = e.detail?.silent ?? false;
      this.reset({ silent });
    }) as EventListener);

    this.element.addEventListener("filterAutocomplete:requestState", () => {
      this.element.dispatchEvent(
        new CustomEvent("filterAutocomplete:state", {
          bubbles: true,
          detail: {
            value: this.input?.value || "",
          },
        }),
      );
    });

    this.element.addEventListener("filterAutocomplete:setState", ((
      e: CustomEvent,
    ) => {
      const { value, silent = false } = e.detail as {
        value: string;
        silent?: boolean;
      };
      if (this.input && value !== undefined) {
        this.input.value = value;
        if (!silent) {
          this.input.dispatchEvent(new Event("input", { bubbles: true }));
        }
      }
    }) as EventListener);
  }

  private reset(options?: { silent?: boolean }): void {
    const silent = options?.silent ?? false;
    if (!this.input) return;
    this.input.value = "";
    if (!silent) {
      this.input.dispatchEvent(new Event("input", { bubbles: true }));
      this.input.dispatchEvent(new Event("change", { bubbles: true }));
    }
    this.collapse();
  }

  protected bindDisclosureEvents(): void {
    // Override to prevent default click-to-toggle behavior
    // We only want to expand when typing matches
  }

  private bindAutocompleteEvents(): void {
    if (!this.input) return;

    this.input.addEventListener("input", () => {
      this.filterOptions();
    });

    this.listItems?.forEach((option) => {
      option.addEventListener("click", (e) => {
        e.preventDefault();
        this.selectOption(option);
      });
    });
  }

  private filterOptions(): void {
    if (!this.input || !this.listItems) return;

    const searchTerm = this.input.value.toLowerCase().trim();

    if (searchTerm.length === 0) {
      this.collapse();
      return;
    }

    let hasVisibleOptions = false;

    this.listItems.forEach((option) => {
      const text = (option.textContent || "").toLowerCase();
      const matches = text.includes(searchTerm);

      if (matches) {
        option.style.display = "";
        hasVisibleOptions = true;
      } else {
        option.style.display = "none";
      }
    });

    if (hasVisibleOptions) {
      if (!this.isExpanded()) {
        this.expand();
      }
    } else {
      this.collapse();
    }
  }

  private selectOption(option: HTMLElement): void {
    if (!this.input) return;

    const value = option.dataset.value || option.textContent?.trim() || "";
    this.input.value = value;

    this.input.dispatchEvent(new Event("input", { bubbles: true }));
    this.input.dispatchEvent(new Event("change", { bubbles: true }));

    this.element.dispatchEvent(
      new CustomEvent("filterAutocomplete:select", {
        bubbles: true,
        detail: {
          value: value,
        },
      }),
    );

    this.collapse();
  }

  public collapse(): void {
    super.collapse();
  }

  private handleDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!this.element.contains(target) && this.isExpanded()) {
      this.collapse();
    }
  }

  destroy(): void {
    super.destroy();
    document.removeEventListener("click", this.documentClickHandler);
  }
}

PluginRegistry.register("filterAutocomplete", FilterAutocomplete);
